import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import frCommon from './resources/fr/common.json';
import enCommon from './resources/en/common.json';
/**
 * Interface language. NOT the content authoring locale — the two are separate
 * concerns and must never share a control (§6.3).
 *
 * `fr-CA` is the source language and the default. This is a Québec product built
 * `lang="fr-CA"`; English is the translation, not the original. That inverts the
 * back office's EN-first convention deliberately — an admin tool used by two
 * founders and a parent-facing product in Québec are not the same audience.
 *
 * Brand vocabulary stays French in both bundles: « Mat Bot », « La Boussole »,
 * « Tempête émotionnelle » are names, not strings to translate.
 */
export const SUPPORTED_LOCALES = ['fr', 'en'];
export const DEFAULT_LOCALE = 'fr';
export const LOCALE_STORAGE_KEY = 'lb-lang';
/**
 * The URL prefix each language is served under.
 *
 * French — the source language — lives at the root. English is prefixed. That
 * asymmetry is deliberate: the default should have the canonical URL, and
 * `/fr/` would make every existing link a redirect for no gain.
 */
export const LOCALE_PREFIX = {
    fr: '',
    en: '/en',
};
/** The locale a pathname is served under, and the path with the prefix removed.
 *
 * `/fr` is accepted as an ALIAS for the unprefixed French root — French links
 * shared as `/fr/resources/courses/{id}` used to 404 because the router had no
 * basepath that matched the prefix. Recognising it here lets `basepathFor`
 * mount the tree under `/fr` when the URL asks for it, without adding `/fr`
 * to `LOCALE_PREFIX` (which would break every existing FR link). Canonical is
 * still the unprefixed form, and `pathInLocale` still produces it.
 */
export function splitLocalePath(pathname) {
    if (pathname === '/en' || pathname.startsWith('/en/')) {
        return { locale: 'en', path: pathname.slice(3) || '/' };
    }
    if (pathname === '/fr' || pathname.startsWith('/fr/')) {
        return { locale: 'fr', path: pathname.slice(3) || '/' };
    }
    return { locale: 'fr', path: pathname };
}
/**
 * The router basepath for a pathname — the literal prefix the URL was fetched
 * under, or `undefined` for the unprefixed root.
 *
 * Distinct from `LOCALE_PREFIX[locale]` because French has two forms: the
 * canonical unprefixed root (`LOCALE_PREFIX.fr === ''`) and the `/fr` alias
 * that a shared link may carry. The router needs the LITERAL prefix so the
 * route tree resolves, and link generation needs the canonical prefix so we
 * don't rewrite everyone's URLs. Same locale, two roles.
 */
export function basepathFor(pathname) {
    if (pathname === '/en' || pathname.startsWith('/en/'))
        return '/en';
    if (pathname === '/fr' || pathname.startsWith('/fr/'))
        return '/fr';
    return undefined;
}
/** The same page in another language. `/journey` ⇄ `/en/journey`. */
export function pathInLocale(pathname, locale) {
    const { path } = splitLocalePath(pathname);
    return `${LOCALE_PREFIX[locale]}${path === '/' ? '/' : path}`;
}
/** `fr-CA` for `<html lang>` and for `Intl`. `fr` is the resource bundle key. */
export const HTML_LANG = {
    fr: 'fr-CA',
    en: 'en-CA',
};
export const resources = {
    fr: { common: frCommon },
    en: { common: enCommon },
};
export function resolveInitialLocale() {
    if (typeof navigator === 'undefined')
        return DEFAULT_LOCALE;
    /*
     * The PATH decides the language before anything else.
     *
     * `/en/…` is a real URL that a crawler fetches and a static host answers with
     * English HTML. It has to win over a stored preference, or a link shared in
     * English would silently flip to French for anyone who had once chosen it —
     * the shared link would not be the page the sender saw.
     */
    try {
        if (window.location.pathname === '/en' || window.location.pathname.startsWith('/en/')) {
            return 'en';
        }
    }
    catch {
        // No location (SSR/tests). Fall through.
    }
    /*
     * `?lang=` still works, as the form a hand-written link is likely to take.
     * It navigates rather than just switching, so the URL and the page agree.
     */
    try {
        const requested = new URLSearchParams(window.location.search).get('lang');
        if (requested === 'fr' || requested === 'en') {
            // eslint-disable-next-line no-restricted-globals -- the documented owner of this key (§6.5)
            localStorage.setItem(LOCALE_STORAGE_KEY, requested);
            return requested;
        }
    }
    catch {
        // No URL, or storage refused. Fall through.
    }
    /*
     * ── The URL is the language. `navigator.language` is deliberately NOT
     * consulted. ────────────────────────────────────────────────────────────
     *
     * It used to be, and once each language became a real path that turned
     * incoherent: `/` is the FRENCH page, prerendered in French and declared
     * canonical — and an English browser opening it re-rendered the whole thing
     * in English. The URL said one language, the document said another, and
     * `/` and `/en/` became the same page for that visitor, which is exactly
     * what `hreflang` exists to prevent.
     *
     * So the path decides, and nothing overrides it. An English-speaking visitor
     * landing on `/` gets French — correct for a Québec product whose source
     * language is French — and the switcher in the header is how they move,
     * which is why that control had to exist on the public pages too.
     *
     * A stored preference is not consulted here either, for the same reason:
     * following a French link should show the French page even to somebody who
     * once chose English. Switching navigates, so the preference and the URL
     * never disagree.
     */
    return DEFAULT_LOCALE;
}
export async function initI18n(locale = resolveInitialLocale()) {
    await i18next.use(initReactI18next).init({
        resources,
        lng: locale,
        fallbackLng: DEFAULT_LOCALE,
        defaultNS: 'common',
        interpolation: { escapeValue: false },
        // A missing key must be visible in development rather than rendering as the
        // key itself in front of a member.
        saveMissing: false,
        returnNull: false,
    });
    /*
     * `<html lang>` must match what actually renders.
     *
     * The document ships `lang="fr-CA"` in the markup, which is right for the
     * default and wrong the moment the resolved locale is English — and it was
     * wrong silently, because every string was hardcoded French and nothing
     * disagreed with the attribute. A screen reader takes this literally and
     * reads English words with French phonetics.
     */
    if (typeof document !== 'undefined') {
        document.documentElement.lang = HTML_LANG[locale];
    }
    return i18next;
}
/**
 * Change the interface language, and remember it.
 *
 * ── Persisted, because the alternative is a control that undoes itself ────
 * `resolveInitialLocale` reads `navigator.language` when nothing is stored, so
 * without writing the choice a member on an English browser would pick French,
 * reload, and be back in English with no way to tell why. The stored value is
 * what makes the switch a preference rather than a gesture.
 *
 * `<html lang>` moves with it. It is not decoration: it decides screen-reader
 * pronunciation, hyphenation and `:lang()` matching, and leaving it at `fr-CA`
 * while the page renders English makes a screen reader read English words with
 * French phonetics.
 *
 * **This is the INTERFACE language and never the content locale.** The two are
 * separate concerns and must not share a control (§6.3) — the API resolves
 * content locale from `Accept-Language` with its own fallback rules, and a
 * member reading French content in an English interface is a legitimate state.
 */
export async function setUiLocale(locale) {
    await i18next.changeLanguage(locale);
    document.documentElement.lang = HTML_LANG[locale];
    try {
        // eslint-disable-next-line no-restricted-globals -- the documented owner of this key (§6.5)
        localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    }
    catch {
        // Private mode. The choice still applies for this session.
    }
}
/**
 * Has this member ever chosen a language HERE, explicitly?
 *
 * The distinction the server's nullable `locale` exists to preserve, mirrored
 * on the client. `resolveInitialLocale` will happily hand back `'en'` because
 * of `navigator.language`, and that is a guess — writing a guess to somebody's
 * account is how a hotel computer with an English browser silently changes the
 * language their security mail arrives in.
 *
 * So: **write on an explicit toggle, never on a default.** This is what tells
 * the two apart.
 */
export function hasStoredUiLocale() {
    try {
        // eslint-disable-next-line no-restricted-globals -- the documented owner of this key (§6.5)
        return localStorage.getItem(LOCALE_STORAGE_KEY) !== null;
    }
    catch {
        /* Private mode: nothing is stored and nothing can be, so there is no
           explicit choice to report. */
        return false;
    }
}
/** The label for each language, written IN that language — never translated. */
export const LOCALE_LABEL = {
    fr: 'Français',
    en: 'English',
};
export { i18next };
export { useTranslation, Trans } from 'react-i18next';
//# sourceMappingURL=index.js.map